import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, CircularProgress, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';

import Modal, { ModalContents, ModalTitle, ModalTypography } from 'components/Modal/Modal';
import { selectUser } from 'entities/user/selectors';
import { Privileges } from 'utils/privileges';
import Img from 'retired/Img';
import documentSvg from 'assets/document.svg';
import {
  NonprofitAddressChangeRequestStatus,
  useRequestNonprofitAddressChangeMutation,
} from 'generated/graphql';
import { showErrorAction } from 'containers/Notification/actions';
import config from 'config';
import { generateEmailLink, generateEncodedEmailBody } from 'utils/supportEmailTemplate';
import LocationObject from 'entities/locationObject/model';
import { renderAddress } from 'utils/renderAddress';
import { updatePropertyAction } from 'entities/user/actions';

import { clearAddressChangeAction } from './actions';

interface FormData {
  reason: string;
}

export const RequestNonprofitAddressChange = ({ newAddress }: { newAddress: LocationObject }) => {
  const [success, setSuccess] = useState(false);

  const dispatch = useDispatch();

  const [requestNonprofitAddressChange, { loading }] = useRequestNonprofitAddressChangeMutation({
    onError: () => {
      dispatch(showErrorAction('Failed to submit a request'));
    },
  });

  const user = useSelector(selectUser);

  const onClose = () => {
    dispatch(clearAddressChangeAction());
  };
  const {
    handleSubmit,
    control,
    formState: { errors, isValid },
  } = useForm<FormData>({
    defaultValues: {
      reason: '',
    },
    mode: 'onChange',
  });

  const isCompany = user?.organization?.isCompany();

  if (!user || isCompany || !user?.hasPrivilege(Privileges.ORGANIZATION_MANAGE_SETTINGS)) {
    return null;
  }

  const onSubmit = async (data: FormData) => {
    const response = await requestNonprofitAddressChange({
      variables: { newAddress, reason: data.reason },
    });
    if ((response as { errors: unknown }).errors) {
      dispatch(showErrorAction('Failed to submit a request'));
    } else {
      dispatch(
        // @NOTE-DP: optimistic update
        updatePropertyAction(
          ['additionalData', 'nonprofitAddressChangeRequests'],
          [
            {
              nonprofit: user.organization.id,
              status: NonprofitAddressChangeRequestStatus.Pending,
              requestedAt: new Date(),
              newAddress,
            },
          ]
        )
      );
      setSuccess(true);
    }
  };

  const emailBody = `
I just submitted a request to change the address of "${
    user?.organization?.name
  }" to "${JSON.stringify(newAddress)}".

I would like to...

`;

  return (
    <Modal style={{ zIndex: 1200 }} open fullWidth onClose={onClose}>
      <ModalContents
        onClickClose={onClose}
        headerStyles={{ paddingTop: 32, paddingBottom: 32 }}
        header={
          <Img
            src={documentSvg}
            alt=""
            style={{
              width: 600,
              height: 145,
            }}
          />
        }
        body={
          <Box mb={4}>
            <ModalTitle>Request to change your nonprofit&apos;s address</ModalTitle>
            {success ? (
              <>
                <ModalTypography>
                  Thanks! Our team will review your submission as soon as possible. Our standard
                  review process takes 3-4 business days.
                </ModalTypography>
                <ModalTypography>
                  You will receive an email notification once the review is complete. If approved,
                  your nonprofit&apos;s address will be updated in our system, and you will receive
                  a confirmation email. If additional information is needed, we will reach out to
                  you directly via your listed email {user?.email}
                </ModalTypography>
                <ModalTypography>
                  If you have any questions or need further assistance, please contact{' '}
                  <a
                    href={generateEmailLink({
                      recipient: config.nonprofitSuccessTeamEmail,
                      subject: `Questions about changing address for ${user.organization.name}`,
                      emailBody: generateEncodedEmailBody({ user, emailBody }),
                    })}
                  >
                    Nonprofit Success Team
                  </a>{' '}
                  for assistance.
                </ModalTypography>
              </>
            ) : (
              <>
                <ModalTypography>
                  To protect your nonprofit and your donors, we review all nonprofit address change
                  requests before processing them. This review usually takes 3-4 business days, and
                  you will be notified by email when the address change is completed.
                </ModalTypography>
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="space-between"
                  rowGap="16px"
                >
                  <TextField
                    disabled
                    value={renderAddress(newAddress)}
                    label="Requested address"
                    helperText="This is the address you are requesting to set."
                    fullWidth
                    variant="standard"
                  />
                  <Controller
                    name="reason"
                    control={control}
                    rules={{
                      required: 'Please enter a reason for the address change',
                      maxLength: 300,
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Reason"
                        helperText="Why do you want to change the nonprofit's address?"
                        fullWidth
                        required
                        variant="standard"
                        error={Boolean(errors.reason)}
                      />
                    )}
                  />
                </Box>
              </>
            )}
          </Box>
        }
        actions={
          <div>
            {loading ? (
              <CircularProgress size={24} />
            ) : success ? (
              <Button
                color="primary"
                type="button"
                variant="outlined"
                style={{ marginRight: 8 }}
                onClick={onClose}
              >
                Close
              </Button>
            ) : (
              <Button
                color="primary"
                type="button"
                variant="contained"
                style={{ marginRight: 8 }}
                onClick={handleSubmit(onSubmit)}
                disabled={!isValid}
              >
                Submit Request
              </Button>
            )}
          </div>
        }
      />
    </Modal>
  );
};
